import { useState } from "react";
import { Sparkles, Wrench, ShieldCheck, ArrowRight, AlertTriangle, TrendingUp, Package, FlaskConical, Pencil, ChevronDown, Save } from "lucide-react";

const A = {
  varenr: "13990",
  count: 4,
  customers: 2,
  latest: "12-05-2026",
  summary:
    "Varenr. 13990 er reklameret 4 gange over tre måneder, primært fra Muuto og HAY. Fejlene samler sig om overfladeridser og afskalning af lak efter slibning. Mønstret peger på en ustabil slibe- og lakproces frem for enkeltstående hændelser.",
  root_cause:
    "Manglende objektiv kontrol af slibeniveau før lakering betyder, at emner med for grov overflade sendes videre og giver dårlig lakhæftning.",
  five_whys: [
    { why: "Hvorfor reklameres 13990 gentagne gange?", because: "Lakken ridser og skaller af kort efter levering." },
    { why: "Hvorfor skaller lakken af?", because: "Lakken hæfter dårligt på overfladen." },
    { why: "Hvorfor hæfter lakken dårligt?", because: "Overfladen er uensartet slebet før lakering." },
    { why: "Hvorfor er slibningen uensartet?", because: "Der findes ingen fast måling af ruhed/slibeniveau før lak." },
    { why: "Hvorfor mangler målingen?", because: "Proceskortet for slibning har ingen defineret kontrolværdi." },
  ],
  patterns: ["Muuto: 3 af 4", "Fejltype: overflade", "Efter slibning", "Q1 2026"],
  prior_korrigerende: ["Ekstra visuel slutkontrol indført (REK-2026-D01)"],
  prior_forebyggende: ["Instruks om omhu ved slibning udsendt"],
  recommendations: {
    korrigerende: [
      "Gennemgå de 4 reklamerede emner og verificér, om den indførte ekstra visuelle slutkontrol faktisk udføres konsekvent.",
      "Opdatér proceskortet for slibning og lakering af 13990 med en konkret, målbar ruhedsværdi før lakering.",
    ],
    forebyggende: [
      "Etablér objektiv ruhedsmåling (fx måleinstrument) som fast trin før lak på alle lakerede stålemner.",
      "Indfør stikprøve på lakhæftning efter hærdning, så afskalning fanges før forsendelse.",
    ],
  },
};

const HISTORY = [
  { complaint_no: "REK-2026-D01", customer_name: "Muuto", status: "Godkendt", created_at: "12-05-2026", description: "Synlige ridser i overflade ved levering.", defects: ["Overflade", "Ridser"] },
  { complaint_no: "REK-2026-C14", customer_name: "Muuto", status: "Afsluttet", created_at: "28-03-2026", description: "Lak skaller af på kant.", defects: ["Lak", "Afskalning"] },
  { complaint_no: "REK-2026-B07", customer_name: "HAY", status: "Under behandling", created_at: "09-02-2026", description: "Uens overflade efter slibning.", defects: ["Overflade"] },
  { complaint_no: "REK-2026-A02", customer_name: "Muuto", status: "Afsluttet", created_at: "15-01-2026", description: "Ridser konstateret ved montage.", defects: ["Ridser"] },
];

const STATUS_CLS = {
  "Godkendt": "bg-[#E7EADF] text-[#5f6b4a] border-[#D3D8C6]",
  "Afsluttet": "bg-[#E7EADF] text-[#5f6b4a] border-[#D3D8C6]",
  "Under behandling": "bg-[#F4EFEC] text-[#806B61] border-[#E6DDD6]",
};

const Kpi = ({ icon: Icon, label, value }) => (
  <div className="pcard p-3.5 flex items-center gap-3">
    <div className="h-9 w-9 rounded-xl bg-[#F3F1EC] flex items-center justify-center shrink-0"><Icon className="h-4 w-4 text-stone-500" /></div>
    <div><p className="text-[11px] uppercase tracking-[0.12em] text-stone-400">{label}</p><p className="text-lg font-medium text-[#1A1919] leading-tight">{value}</p></div>
  </div>
);

const RootCause = () => (
  <div className="rounded-xl bg-[#FBF1EF] border border-[#E9C9C3] p-4">
    <p className="label-caps text-[#A83232] mb-1">Sandsynlig rodårsag</p>
    <p className="text-sm text-[#7a3b36] font-medium leading-relaxed">{A.root_cause}</p>
  </div>
);

const FiveWhys = () => (
  <div className="relative">
    <div className="absolute left-3 top-2 bottom-2 w-px bg-[#EDE7F6]" />
    <div className="space-y-3">
      {A.five_whys.map((w, i) => (
        <div key={i} className="flex gap-3 relative">
          <span className="h-6 w-6 rounded-full bg-[#EDE7F6] text-[#6B4FA3] text-xs font-semibold flex items-center justify-center shrink-0 z-10">{i + 1}</span>
          <div className="pb-1">
            <p className="text-sm font-medium text-[#1A1919]">{w.why}</p>
            <p className="text-sm text-stone-500 flex items-start gap-1.5"><ArrowRight className="h-3.5 w-3.5 mt-0.5 shrink-0 text-stone-400" /> {w.because}</p>
          </div>
        </div>
      ))}
    </div>
  </div>
);

const Patterns = () => (
  <div className="flex flex-wrap gap-1.5">{A.patterns.map((p, i) => <span key={i} className="text-xs px-2.5 py-1 rounded-lg bg-[#F3F1EC] text-stone-600 border border-[#E6DDD6]">{p}</span>)}</div>
);

const Actions = () => (
  <div className="grid sm:grid-cols-2 gap-3">
    <div className="rounded-xl border border-[#E6DDD6] bg-[#F9F6F3] p-4">
      <p className="label-caps text-[#806B61] mb-2 flex items-center gap-1.5"><Wrench className="h-3.5 w-3.5" /> Korrigerende</p>
      <ul className="space-y-2">{A.recommendations.korrigerende.map((x, i) => <li key={i} className="text-sm text-stone-700 flex gap-1.5"><span className="text-[#806B61]">•</span> {x}</li>)}</ul>
    </div>
    <div className="rounded-xl border border-[#D3D8C6] bg-[#F6F8F1] p-4">
      <p className="label-caps text-[#5f6b4a] mb-2 flex items-center gap-1.5"><ShieldCheck className="h-3.5 w-3.5" /> Forebyggende</p>
      <ul className="space-y-2">{A.recommendations.forebyggende.map((x, i) => <li key={i} className="text-sm text-stone-700 flex gap-1.5"><span className="text-[#5f6b4a]">•</span> {x}</li>)}</ul>
    </div>
  </div>
);

const Assessment = () => (
  <div className="rounded-xl border border-[#EBE8E3] bg-[#FBFAF8] p-4 space-y-4">
    <p className="label-caps flex items-center gap-1.5"><Pencil className="h-3.5 w-3.5" /> Din vurdering & handling</p>
    <div>
      <label className="text-xs font-medium text-stone-600 mb-1 block">Aktiv handling</label>
      <textarea className="w-full border border-[#EBE8E3] rounded-lg bg-white resize-y min-h-[60px] text-sm p-2.5 outline-none focus:border-[#A83232]" placeholder="Beskriv den konkrete handling I iværksætter…" />
    </div>
    <div className="grid sm:grid-cols-2 gap-3">
      <div><label className="text-xs font-medium text-stone-600 mb-1 block">Korrigerende (egen)</label><textarea className="w-full border border-[#EBE8E3] rounded-lg bg-white resize-y min-h-[80px] text-sm p-2.5 outline-none focus:border-[#A83232]" /></div>
      <div><label className="text-xs font-medium text-stone-600 mb-1 block">Forebyggende (egen)</label><textarea className="w-full border border-[#EBE8E3] rounded-lg bg-white resize-y min-h-[80px] text-sm p-2.5 outline-none focus:border-[#A83232]" /></div>
    </div>
    <div className="flex justify-end"><button className="rounded-xl bg-[#A83232] hover:bg-[#8f2a2a] text-white text-sm font-medium px-4 py-2 flex items-center gap-1.5"><Save className="h-4 w-4" /> Gem analyse</button></div>
  </div>
);

const Summary = () => <p className="text-sm text-stone-700 leading-relaxed">{A.summary}</p>;

const HistoryBar = () => (
  <div className="pcard p-3.5 flex items-center gap-3 text-sm text-stone-600">
    <Package className="h-4 w-4 text-stone-400 shrink-0" />
    <span><span className="font-medium text-[#1A1919]">{A.count} reklamationer</span> · {HISTORY.map((h) => h.customer_name).filter((v, i, s) => s.indexOf(v) === i).join(", ")} · seneste {A.latest}</span>
  </div>
);

const HistoryList = () => (
  <div className="space-y-3">
    {HISTORY.map((r, i) => (
      <div key={i} className="rounded-xl border border-[#F0EDE8] p-3.5">
        <div className="flex items-center justify-between gap-2 mb-1.5">
          <span className="font-mono text-xs font-semibold text-[#A83232]">{r.complaint_no}</span>
          <div className="flex items-center gap-2">
            <span className={`text-[11px] px-2 py-0.5 rounded-full border ${STATUS_CLS[r.status] || "bg-stone-100 text-stone-600 border-stone-200"}`}>{r.status}</span>
            <span className="text-xs text-stone-400">{r.created_at}</span>
          </div>
        </div>
        <p className="text-xs text-stone-500 mb-1">{r.customer_name}</p>
        <p className="text-sm text-stone-700">{r.description}</p>
        {r.defects.length > 0 && <div className="flex flex-wrap gap-1.5 mt-2">{r.defects.map((d, j) => <span key={j} className="text-[11px] px-2 py-0.5 rounded-md bg-[#F4EFEC] text-[#806B61] border border-[#E6DDD6]">{d}</span>)}</div>}
      </div>
    ))}
  </div>
);

const SectionTitle = ({ children }) => <p className="label-caps mb-3">{children}</p>;
const Divider = () => <div className="border-t border-[#F0EDE8]" />;

// ---------------- Variant 1: Fuld-bredde rapport ----------------
const VariantFullWidth = () => (
  <div className="space-y-5">
    <HistoryBar />
    <div className="pcard p-6 space-y-6" data-testid="variant-fullwidth">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-medium text-[#1A1919] flex items-center gap-2"><Sparkles className="h-4 w-4 text-[#6B4FA3]" /> AI-årsagsanalyse</h2>
        <span className="text-[10px] font-semibold text-[#6B4FA3] bg-[#EDE7F6] border border-[#DDD2EF] rounded-full px-2 py-0.5">5×HVORFOR</span>
      </div>
      <div><SectionTitle>Sammenfatning</SectionTitle><Summary /></div>
      <RootCause />
      <Divider />
      <div><SectionTitle>5 × Hvorfor</SectionTitle><FiveWhys /></div>
      <Divider />
      <div><SectionTitle>Mønstre</SectionTitle><Patterns /></div>
      <Divider />
      <div><SectionTitle>Anbefalede handlinger</SectionTitle><Actions /></div>
      <Divider />
      <Assessment />
    </div>
  </div>
);

// ---------------- Variant 2: Faner ----------------
const TABS = ["Sammenfatning", "5×Hvorfor", "Mønstre", "Handlinger", "Din vurdering"];
const VariantTabs = () => {
  const [tab, setTab] = useState("5×Hvorfor");
  return (
    <div className="grid lg:grid-cols-[0.85fr_1.15fr] gap-6 items-start">
      <div className="pcard p-5"><h2 className="text-lg font-medium text-[#1A1919] mb-4">Reklamationshistorik</h2><HistoryList /></div>
      <div className="pcard p-5 space-y-4" data-testid="variant-tabs">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-medium text-[#1A1919] flex items-center gap-2"><Sparkles className="h-4 w-4 text-[#6B4FA3]" /> AI-årsagsanalyse</h2>
        </div>
        <RootCause />
        <div className="flex gap-1 border-b border-[#EBE8E3] -mx-1 px-1 overflow-x-auto">
          {TABS.map((tt) => (
            <button key={tt} onClick={() => setTab(tt)} className={`text-sm px-3 py-2 whitespace-nowrap border-b-2 -mb-px transition-colors ${tab === tt ? "border-[#A83232] text-[#A83232] font-medium" : "border-transparent text-stone-500 hover:text-stone-800"}`}>{tt}</button>
          ))}
        </div>
        <div className="pt-1">
          {tab === "Sammenfatning" && <Summary />}
          {tab === "5×Hvorfor" && <FiveWhys />}
          {tab === "Mønstre" && <Patterns />}
          {tab === "Handlinger" && <Actions />}
          {tab === "Din vurdering" && <Assessment />}
        </div>
      </div>
    </div>
  );
};

// ---------------- Variant 3: Accordion ----------------
const ACC = [
  { id: "sum", title: "Sammenfatning", render: () => <Summary /> },
  { id: "root", title: "Rodårsag", render: () => <RootCause /> },
  { id: "whys", title: "5 × Hvorfor", render: () => <FiveWhys /> },
  { id: "pat", title: "Mønstre", render: () => <Patterns /> },
  { id: "act", title: "Korrigerende & Forebyggende", render: () => <Actions /> },
  { id: "ass", title: "Din vurdering & handling", render: () => <Assessment /> },
];
const VariantAccordion = () => {
  const [open, setOpen] = useState({ root: true, whys: true });
  const toggle = (id) => setOpen((p) => ({ ...p, [id]: !p[id] }));
  return (
    <div className="space-y-6">
      <div className="pcard p-5" data-testid="acc-history">
        <h2 className="text-lg font-medium text-[#1A1919] mb-4">Reklamationshistorik</h2>
        <div className="grid sm:grid-cols-2 gap-3">
          {HISTORY.map((r, i) => (
            <div key={i} className="rounded-xl border border-[#F0EDE8] p-3.5">
              <div className="flex items-center justify-between gap-2 mb-1.5">
                <span className="font-mono text-xs font-semibold text-[#A83232]">{r.complaint_no}</span>
                <div className="flex items-center gap-2">
                  <span className={`text-[11px] px-2 py-0.5 rounded-full border ${STATUS_CLS[r.status] || "bg-stone-100 text-stone-600 border-stone-200"}`}>{r.status}</span>
                  <span className="text-xs text-stone-400">{r.created_at}</span>
                </div>
              </div>
              <p className="text-xs text-stone-500 mb-1">{r.customer_name}</p>
              <p className="text-sm text-stone-700">{r.description}</p>
              {r.defects.length > 0 && <div className="flex flex-wrap gap-1.5 mt-2">{r.defects.map((d, j) => <span key={j} className="text-[11px] px-2 py-0.5 rounded-md bg-[#F4EFEC] text-[#806B61] border border-[#E6DDD6]">{d}</span>)}</div>}
            </div>
          ))}
        </div>
      </div>
      <div className="pcard px-5 py-2" data-testid="variant-accordion">
        <div className="flex items-center justify-between py-3">
          <h2 className="text-lg font-medium text-[#1A1919] flex items-center gap-2"><Sparkles className="h-4 w-4 text-[#6B4FA3]" /> AI-årsagsanalyse</h2>
          <span className="text-[10px] font-semibold text-[#6B4FA3] bg-[#EDE7F6] border border-[#DDD2EF] rounded-full px-2 py-0.5">5×HVORFOR</span>
        </div>
        {ACC.map((s) => (
          <div key={s.id} className="border-t border-[#F0EDE8]">
            <button onClick={() => toggle(s.id)} className="w-full flex items-center justify-between py-3.5 text-left">
              <span className="text-sm font-medium text-[#1A1919]">{s.title}</span>
              <ChevronDown className={`h-4 w-4 text-stone-400 transition-transform ${open[s.id] ? "rotate-180" : ""}`} />
            </button>
            {open[s.id] && <div className="pb-4 pt-0.5">{s.render()}</div>}
          </div>
        ))}
      </div>
    </div>
  );
};

export default function AnalyseLayoutPreview() {
  const [v, setV] = useState("1");
  const opts = [
    { id: "1", label: "1 · Fuld-bredde rapport" },
    { id: "2", label: "2 · Faner" },
    { id: "3", label: "3 · Accordion" },
  ];
  return (
    <div className="min-h-screen bg-[#FBFAF8]">
      <div className="sticky top-0 z-20 bg-[#FBFAF8]/90 backdrop-blur border-b border-[#EBE8E3]">
        <div className="max-w-6xl mx-auto px-6 py-4 flex flex-wrap items-center gap-4 justify-between">
          <div>
            <p className="label-caps mb-0.5">Preview · Reklamationsanalyse</p>
            <h1 className="text-2xl font-light tracking-tight text-[#1A1919]">Vælg layout</h1>
          </div>
          <div className="flex gap-1.5 p-1 rounded-xl bg-[#F3F1EC] border border-[#EBE8E3]">
            {opts.map((o) => (
              <button key={o.id} data-testid={`preview-tab-${o.id}`} onClick={() => setV(o.id)} className={`text-sm px-3.5 py-1.5 rounded-lg transition-colors ${v === o.id ? "bg-white text-[#A83232] font-medium shadow-sm" : "text-stone-500 hover:text-stone-800"}`}>{o.label}</button>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-8">
        <div className="rounded-2xl border border-[#E9C9C3] bg-[#FBF1EF] p-4 mb-6 flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-[#A83232] shrink-0 mt-0.5" />
          <div><p className="font-semibold text-[#A83232]">Aktiv handling påkrævet</p><p className="text-sm text-[#8f4a44] mt-0.5">Varenr. {A.varenr} er reklameret {A.count} gange. Der bør iværksættes en aktiv handling.</p></div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
          <Kpi icon={Package} label="Reklamationer" value={A.count} />
          <Kpi icon={TrendingUp} label="Kunder" value={A.customers} />
          <Kpi icon={FlaskConical} label="Seneste" value={A.latest} />
          <Kpi icon={Sparkles} label="Analyse" value="Klar" />
        </div>

        {v === "1" && <VariantFullWidth />}
        {v === "2" && <VariantTabs />}
        {v === "3" && <VariantAccordion />}

        <p className="text-xs text-stone-400 text-center mt-10">Preview med eksempeldata · rigtige farver og skrifttype · vælg den opbygning du bedst kan lide</p>
      </div>
    </div>
  );
}
